import { useState, useMemo, useCallback, useEffect } from 'react';
import RefereeSelector from '../../components/RefereeSelector';
import DataTable from '../../components/DataTable';
import AvatarName from '../../components/AvatarName';
import EditDrawer from '../../pages/Referee/EditDrawer';

import request from '@/request';

const roles = [
  { key: 'chief', label: '裁判长', multiple: false },
  { key: 'vice', label: '副裁判长', multiple: true },
  { key: 'project', label: '项目裁判', multiple: false },
];

const IconButton = ({ onClick, text, className }) => (
  <button
    type="button"
    className={`flex items-center gap-1 ${className}`}
    onClick={onClick}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className="w-4 h-4"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 20v-1a4 4 0 014-4h4"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11v2m1-1h-2" />
    </svg>
    <span>{text}</span>
  </button>
);

export default function Referees({ event, items }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState('project');
  const [tempSelected, setTempSelected] = useState([]);
  const [selectedItem, setSelectedItem] = useState([]);
  const [eventReferees, setEventReferees] = useState([]);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);

  const showAssignDrawer = (roleKey) => {
    setCurrentRole(roleKey);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setCurrentRole(null);
    setTempSelected([]);
  };

  const loadEventReferees = useCallback(() => {
    request
      .get(
        `/events/admin/referee/invite/list?page=1&pageSize=999&eventId=${event.id}`,
        {
          eventId: event.id,
        }
      )
      .then((res) => {
        setEventReferees(res.items || []);
      });
  }, [event.id]);

  useEffect(() => {
    loadEventReferees();
  }, [loadEventReferees]);

  const toggleSelect = (referee, multiple) => {
    if (multiple) {
      setTempSelected((prev) =>
        prev.some((r) => r.id === referee.id)
          ? prev.filter((r) => r.id !== referee.id)
          : [...prev, referee]
      );
    } else {
      setTempSelected((prev) =>
        prev.some((r) => r.id === referee.id) ? [] : [referee]
      );
    }
  };

  const confirmAssign = useCallback(async () => {
    let role = 1;
    if (currentRole === 'vice') role = 0;
    if (currentRole === 'project') role = 2;
    await request.post('/events/admin/org/referee/role', {
      referees: tempSelected.map((r) => ({
        refereeId: r.refereeId,
        itemIds: [selectedItem.id],
        role,
      })),
      eventId: event.id,
    });
    loadEventReferees();
    closeDrawer();
  }, [currentRole, tempSelected, selectedItem, event.id, loadEventReferees]);

  const currentRoleObj = useMemo(
    () => roles.find((r) => r.key === currentRole),
    [currentRole]
  );

  const getAssignedReferees = (index) => {
    return (
      eventReferees.filter((er) => er.role === (index === 0 ? 1 : 0)) || null
    );
  };

  const columns = [
    {
      title: '项目',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '组别',
      dataIndex: 'groupName',
      key: 'groupName',
    },
    {
      title: '执行裁判',
      dataIndex: 'referee',
      render: (_, record) => {
        const r = eventReferees.find(
          (item) => item.role === 2 && item.itemIds.find((e) => e === record.id)
        );
        return r ? (
          <AvatarName name={r.refereeName} />
        ) : (
          <div className="text-sm text-slate-400">未指定</div>
        );
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      align: 'left',
      render: (_, record) => (
        <IconButton
          text="去指定"
          className="text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-700 px-2 py-0.5 rounded cursor-pointer transition"
          onClick={() => {
            setSelectedItem(record);
            showAssignDrawer('project', record.key);
          }}
        />
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">裁判安排</h2>
        <div className="text-sm text-slate-500">点击“去指定”分配人员</div>
      </div>

      <div className="flex items-center justify-between gap-4">
        {roles.map((role, index) =>
          role.key !== 'project' ? (
            <div
              key={role.key}
              className={`p-4 bg-white border border-slate-100 rounded-md shadow-sm ${role.key === 'chief' ? 'w-100' : 'flex-1'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-slate-800 text-left">
                  {role.label}
                </div>
                <div className="text-xs text-slate-400">
                  {role.multiple ? '可多选' : '仅可选一人'}
                </div>
              </div>

              <div className="flex items-center justify-between">
                {getAssignedReferees(index).length ? (
                  <div className="flex items-center justify-start gap-1">
                    {getAssignedReferees(index).map((r) => (
                      <AvatarName name={r.refereeName} key={r.refereeName} />
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-slate-400">尚未指定</div>
                )}
                <IconButton
                  text="去指定"
                  className="inline-flex items-center bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1.5 rounded-md hover:bg-blue-100 transition"
                  onClick={() => showAssignDrawer(role.key)}
                />
              </div>
            </div>
          ) : null
        )}
      </div>

      <DataTable columns={columns} list={items} />

      <RefereeSelector
        open={drawerOpen}
        onClose={closeDrawer}
        onConfirm={confirmAssign}
        title={currentRoleObj ? `选择 - ${currentRoleObj.label}` : '选择裁判'}
        selectedItems={tempSelected}
        onToggle={toggleSelect}
        multiple={currentRoleObj ? currentRoleObj.multiple : false}
        api="/events/admin/referee/invite/list"
        addPath="/referee"
        onCreate={() => {
          setDrawerOpen(false);
          setEditDrawerVisible(true);
        }}
      />
      <EditDrawer
        open={editDrawerVisible}
        onClose={() => {
          setDrawerOpen(true);
          setEditDrawerVisible(false);
        }}
        onSave={() => {
          setDrawerOpen(true);
          setEditDrawerVisible(false);
        }}
      />
    </div>
  );
}
